import React, { useState } from 'react';
import { object, shape, arrayOf, string, bool } from 'prop-types';
import { translate } from '@fiverr-private/i18n-react';
import { updateQueryParams } from '@fiverr-private/variations_display';
import { makersExperience } from '@fiverr-private/logo_maker_analytics';
import { LEARN_MORE_TRACKER_POS } from '../../../shared/constants';
import Teaser from '../../../shared/components/LandingPages/Teaser';
import IdeaTags from '../../../shared/components/LandingPages/IdeaTags';
import Footer from '../../../shared/components/LandingPages/Footer';
import HowDoesItWork from '../../../shared/components/LandingPages/HowDoesItWork';
import { SECTION_KEYS } from '../../../shared/components/LandingPages/HowDoesItWork/constants';
import { useUserAgentContext } from '../../../shared/components/UserAgentContext';
import MakeMyLogoButton from '../../../shared/components/MakeMyLogoButton';
import { useEffectOnce } from '../../../shared/hooks';
import monitor from '../../../service/bigquery';
import { tagLinkClicked, getIdeaPageName, getStartedClicked } from './lib/bi-events';
import { getBriefParams } from './lib';
import { FEATURE_LOGO_OVERLAY_IMAGE } from './propTypes/featuredLogoOverlayImage';
import { RELATED_TAGS } from './propTypes/relatedTags';
import { HERO_CTA } from './propTypes/heroCta';
import { INSPIRATION } from './propTypes/inspiration';
import IntroHeader from './IntroHeader';
import Inspiration from './Inspiration';
import { INNER_CLASSES, TEASER_CONTENT } from './constants';
import './style.scss';

const { reportPageView } = makersExperience.buyerIdeas;

const BuyerIdeasPage = ({
    context: { httpRequest },
    pageData: {
        brandName: initialBrandName = '',
        slogan: initialSlogan = '',
        inspiration,
        heroCta,
        featuredImageAlt,
        featuredLogoOverlayImage,
        tagId,
        tagType,
        tagName,
        relatedTags,
        relatedTagsTitle,
        topLevelIndustries,
        footerTitle,
        howToMakeLogoData,
    },
    tracking: { pageCtxId, refCtxId, uid },
    traffic,
    isServerEntry = false,
}) => {
    const initialBrandNameSlogan = {
        brandName: initialBrandName,
        slogan: initialSlogan || '',
    };
    const [{ brandName, slogan }, setTextFields] = useState(initialBrandNameSlogan);
    const { isTouch } = useUserAgentContext();

    useEffectOnce(() => {
        monitor.trackPageView({
            page: getIdeaPageName(tagType, tagId),
            httpRequest,
            traffic,
        });
        reportPageView();
    });

    const getBiEventTagClickFn = (componentName) => (tag) =>
        tagLinkClicked({
            ...tag,
            componentName,
            pageTagType: tagType,
            pageTagId: tagId,
        });

    const handleTextFieldsChange = ({ brandName, slogan }) => {
        setTextFields({ brandName, slogan });

        // Store brand name and slogan in the query params
        updateQueryParams({
            brand_name: brandName,
            slogan,
        });
    };

    const briefParams = getBriefParams({ slogan, tagId, tagType });
    const trackGetStartedClick = (elementText) =>
        getStartedClicked({
            elementText,
            elementPosition: LEARN_MORE_TRACKER_POS.bottom,
            elementName: 'make your logo',
            pageTagType: tagType,
            pageTagId: tagId,
        });

    return (
        <div className="makers-buyer-ideas-page">
            <section className="app-wrapper">
                <MakeMyLogoButton />
                <IntroHeader
                    heroCta={heroCta}
                    featuredImageAlt={featuredImageAlt}
                    featuredLogoOverlayImage={featuredLogoOverlayImage}
                    brandName={brandName}
                    slogan={slogan}
                    tagId={tagId}
                    tagType={tagType}
                    tagName={tagName}
                />
                <Inspiration
                    title={inspiration.title}
                    inspiration={inspiration}
                    pageCtxId={pageCtxId}
                    refCtxId={refCtxId}
                    tagId={tagId}
                    uid={uid}
                    tagName={tagName}
                    tagType={tagType}
                    brandName={brandName || translate('logo_maker_perseus.general.brand_name_placeholder')}
                    slogan={slogan}
                    isTouch={isTouch}
                    onChange={handleTextFieldsChange}
                    isServerEntry={isServerEntry}
                />
                <IdeaTags
                    tags={relatedTags}
                    expandLabel={translate('logo_maker_perseus.buyer_ideas.related_tags_more_btn')}
                    title={relatedTagsTitle}
                    refCtxId={pageCtxId}
                    onClick={getBiEventTagClickFn('related tag')}
                />
                <HowDoesItWork
                    className={INNER_CLASSES}
                    sectionsKey={SECTION_KEYS.IDEAS}
                    i18nPrefix="logo_maker_perseus.buyer_ideas"
                    isMobileOrTablet={false}
                    tagName={tagName}
                    ideasData={howToMakeLogoData}
                />
                <IdeaTags
                    tags={topLevelIndustries}
                    refCtxId={pageCtxId}
                    expandLabel={translate('logo_maker_perseus.buyer_ideas.top_level_industries_more_btn')}
                    title={translate('logo_maker_perseus.buyer_ideas.top_level_industries_title')}
                    onClick={getBiEventTagClickFn('browse by industry')}
                />
                <Teaser
                    className={INNER_CLASSES}
                    sections={TEASER_CONTENT}
                    i18nPrefix="logo_maker_perseus.buyer_landing_page"
                />
                <Footer
                    footerTitle={footerTitle}
                    tagId={tagId}
                    tagType={tagType}
                    brandName={brandName}
                    slogan={slogan}
                    briefParams={briefParams}
                    onSubmit={() => trackGetStartedClick(translate('logo_maker_perseus.general.make_your_logo'))}
                />
            </section>
        </div>
    );
};

BuyerIdeasPage.propTypes = {
    context: shape({
        httpRequest: object,
    }),
    traffic: object,
    tracking: shape({
        pageCtxId: string,
        refCtxId: string,
        uid: string,
    }),
    pageData: shape({
        brandName: string,
        slogan: string,
        featuredImageAlt: string,
        featuredImageSellerName: string,
        featuredImageSellerProfileImage: string,
        featuredLogoOverlayImage: FEATURE_LOGO_OVERLAY_IMAGE,
        heroCta: HERO_CTA,
        inspiration: shape(INSPIRATION),
        tagId: string,
        tagType: string,
        tagName: string,
        relatedTags: arrayOf(RELATED_TAGS),
        relatedTagsTitle: string,
        topLevelIndustries: arrayOf(RELATED_TAGS),
        howToMakeLogoData: shape({
            title: string,
            subtitle: string,
        }),
        footerTitle: string,
    }),
    isServerEntry: bool,
};

export default BuyerIdeasPage;
